import styled from 'styled-components';
import { CONTENT_TYPE_COLOR_MAP, IContentTypeDistributionDB } from 'type/tracking';

import { useGetContentSummary } from 'hooks/queries/tracking';
import CoreLoading from 'shared/CoreLoading';
import CoreSvg from 'shared/CoreSvg';
import Icon from 'shared/icons';
import { ReportSection } from 'styles/styled-components/report';
import style from 'styles/styled-components/style';

interface IContentTypeDistribution {
	code: string;
	forceEmpty?: boolean;
}

const SHORT_NAME_MAP: Record<string, string> = {
	ig_feed: '피드',
	ig_video: '릴스',
	yt_video: '영상',
	yt_shorts: '쇼츠',
	tt_content: '틱톡',
};

const CONTENT_TYPE_ICON: Record<string, JSX.Element> = {
	ig_feed: <Icon.Insta />,
	ig_video: <Icon.InstaReels />,
	yt_video: <Icon.Youtube />,
	yt_shorts: <Icon.YoutubeShorts />,
	tt_content: <Icon.Tiktok />,
};

// 표시 순서: 릴스 → 피드 → 쇼츠 → 영상
const DISPLAY_ORDER = ['ig_video', 'ig_feed', 'yt_shorts', 'yt_video'];

const ContentTypeDistribution = ({ code, forceEmpty }: IContentTypeDistribution) => {
	const { data: contentSummary, isLoading } = useGetContentSummary(code);

	if (isLoading) {
		return (
			<ReportSection _margin="0">
				<Grid flex="center" _height="80px">
					<CoreLoading color_type="black" size_type="large" />
				</Grid>
			</ReportSection>
		);
	}

	const rawDistribution = forceEmpty ? [] : contentSummary?.contentTypeDistribution ?? [];
	const distribution = [...rawDistribution].sort(
		(a, b) => DISPLAY_ORDER.indexOf(a.type) - DISPLAY_ORDER.indexOf(b.type),
	);
	const hasData = distribution.length > 0;

	return (
		<ReportSection _margin="0">
			<Card>
				<SubTitle>콘텐츠당 유형 비율</SubTitle>
				<LegendContainer>
					{hasData
						? distribution.map((item: IContentTypeDistributionDB) => (
								<LegendItem key={item.type}>
									<LegendDot
										$color={CONTENT_TYPE_COLOR_MAP[item.type] ?? '#999999'}
									/>
									<CoreSvg _size="14px">
										{CONTENT_TYPE_ICON[item.type] ?? null}
									</CoreSvg>
									<LegendLabel>
										{SHORT_NAME_MAP[item.type] ?? item.type}
									</LegendLabel>
									<LegendValue>{item.percentage}% ({item.count}건)</LegendValue>
								</LegendItem>
						  ))
						: DISPLAY_ORDER.map((ct) => (
								<LegendItem key={ct}>
									<LegendDot $color={CONTENT_TYPE_COLOR_MAP[ct] ?? '#999999'} />
									<CoreSvg _size="14px">{CONTENT_TYPE_ICON[ct] ?? null}</CoreSvg>
									<LegendLabel>{SHORT_NAME_MAP[ct] ?? ct}</LegendLabel>
									<LegendValue>-</LegendValue>
								</LegendItem>
						  ))}
				</LegendContainer>
				<BarContainer>
					{hasData ? (
						distribution.map((item: IContentTypeDistributionDB) => (
							<BarSegment
								key={item.type}
								$percentage={item.percentage}
								$color={CONTENT_TYPE_COLOR_MAP[item.type] ?? '#999999'}
							/>
						))
					) : (
						<EmptyBar />
					)}
				</BarContainer>
			</Card>
		</ReportSection>
	);
};

const Card = styled.div`
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	border-radius: 8px;
	padding: 24px;
`;

const SubTitle = styled.div`
	${({ theme }) => theme.typo.body.p_bold};
	color: ${({ theme }) => theme.colors.gray.gray80_text};
	margin-bottom: 16px;
`;

const LegendContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
	margin-bottom: 12px;
`;

const LegendItem = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
`;

const LegendDot = styled.div<{ $color: string }>`
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background-color: ${({ $color }) => $color};
	flex-shrink: 0;
`;

const LegendLabel = styled.span`
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray80_text};
`;

const LegendValue = styled.span`
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray50};
`;

const BarContainer = styled.div`
	width: 100%;
	height: 32px;
	display: flex;
	border-radius: 8px;
	overflow: hidden;
`;

const BarSegment = styled.div<{
	$percentage: number;
	$color: string;
}>`
	width: ${({ $percentage }) => $percentage}%;
	height: 100%;
	background-color: ${({ $color }) => $color};
	transition: width 0.3s ease;
`;

const EmptyBar = styled.div`
	width: 100%;
	height: 100%;
	background-color: #f1f3f5;
`;

const { Grid } = style;

export default ContentTypeDistribution;
